import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Package,
  DollarSign,
  Hash,
  TrendingUp,
  Box,
  Edit2,
  Trash2,
  Globe,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import DeleteAlertModal from "../../common/DeleteAlertModal"; 

const AdminProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState("en");

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/products/get-product-by-id/${id}`
        );
        const fetchedProduct = res.data?.product || {};
        setProduct(fetchedProduct);
        setSelectedImage(fetchedProduct.images?.[0] || "");
      } catch (err) {
        console.error("Error fetching product:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  // Helper function to extract text based on active language
  const getText = (field) => {
    if (!field) return "";
    
    if (typeof field === "object" && field !== null) {
      // Check if it's a multilingual object
      if (field[activeLanguage] !== undefined) {
        return field[activeLanguage];
      }
      // Check if it has language keys
      const hasLanguageKeys = Object.keys(field).some(key => 
        ['en', 'hi', 'es', 'zh'].includes(key)
      );
      if (hasLanguageKeys) {
        return field.en || field[Object.keys(field)[0]] || "";
      }
      // If it's a nested object but not multilingual, return the whole object
      return JSON.stringify(field);
    }
    
    // If field is already a string
    if (typeof field === "string") {
      return field;
    }
    
    return "";
  };

  // Helper function to extract English text for category names
  const getEnglishText = (field) => {
    if (!field) return "";
    if (typeof field === "object" && field !== null && field.en !== undefined) {
      return field.en;
    }
    if (typeof field === "string") {
      return field;
    }
    return "";
  };

  // Delete Product Function
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/products/delete-product/${product._id}`
      );

      setShowDeleteModal(false);
      Swal.fire({
        title: "Deleted!",
        text: `${getText(product?.name)} has been deleted successfully.`,
        icon: "success",
        confirmButtonColor: "#3085d6",
      });
      navigate("/admin-products");
    } catch (error) {
      console.error("Delete error:", error);
      Swal.fire({
        title: "Error",
        text: "Something went wrong while deleting the product.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <p className="text-gray-500 animate-pulse text-lg">Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 text-gray-600">
        <p>Product not found</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg"
        >
          Go Back
        </button>
      </div>
    );
  }

  //  Reusable Detail Card
  const DetailCard = ({ icon: Icon, label, value, colorClass = "text-gray-700" }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg bg-opacity-10 ${colorClass}`}>
          <Icon className={`w-5 h-5 ${colorClass}`} />
        </div>
        <span className="text-sm text-gray-500 font-medium">{label}</span>
      </div>
      <p className={`text-lg font-semibold ${colorClass}`}>{value}</p>
    </div>
  );

  // Language Tabs Component
  const LanguageTabs = () => (
    <div className="flex space-x-2 mb-4">
      {["en", "hi", "es", "zh"].map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => setActiveLanguage(lang)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeLanguage === lang
              ? "bg-blue-500 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {lang === "en" ? "English" : 
           lang === "hi" ? "हिंदी" : 
           lang === "es" ? "Español" : 
           "中文"}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Amazon-style Top Header bar */}
      <div className="flex justify-between items-center p-4 border-b border-gray-300 bg-gray-100">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-700 hover:text-black hover:underline text-sm font-medium">
            &larr; Back to Inventory
          </button>
        </div>
        <div className="flex items-center gap-4">
           {/* Language Selector built into header */}
           <div className="flex items-center gap-2">
             <span className="text-sm font-bold text-gray-700">Language:</span>
             <select 
               value={activeLanguage} 
               onChange={(e) => setActiveLanguage(e.target.value)}
               className="border border-gray-300 rounded-sm text-sm px-2 py-1"
             >
               <option value="en">English</option>
               <option value="hi">Hindi</option>
               <option value="es">Spanish</option>
               <option value="zh">Chinese</option>
             </select>
           </div>
          <button onClick={() => navigate(`/edit-product/${product._id}`)} className="px-4 py-1 border border-gray-400 bg-white hover:bg-gray-50 rounded-sm text-sm font-medium shadow-sm">
            Edit Listing
          </button>
          <button onClick={() => setShowDeleteModal(true)} className="px-4 py-1 border border-red-500 text-red-600 bg-white hover:bg-red-50 rounded-sm text-sm font-medium shadow-sm">
            Delete Listing
          </button>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto p-6 grid grid-cols-1 lg:grid-cols-[100px_400px_1fr_300px] gap-8">
        
        {/* Thumbnails (Far Left) */}
        <div className="flex flex-col gap-2">
          {product.images?.map((img, i) => (
            <div
              key={i}
              onClick={() => setSelectedImage(img)}
              className={`w-[60px] h-[60px] border rounded-sm flex items-center justify-center cursor-pointer hover:border-[#b07d17] hover:shadow-sm ${selectedImage === img ? 'border-[#b07d17] border-2 ring-1 ring-[#b07d17]' : 'border-gray-300'}`}
            >
              <img src={img} alt={`thumb-${i}`} className="max-h-full max-w-full object-contain" />
            </div>
          ))}
        </div>

        {/* Main Image */}
        <div className="flex justify-center items-start">
          <img src={selectedImage || product.images?.[0]} alt="Product" className="w-full max-w-[400px] object-contain" />
        </div>

        {/* Center: Title, Price, Details, Features */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-[24px] leading-tight font-medium text-black">
              {getText(product?.name)}
            </h1>
            <a href="#" className="text-[#007185] hover:text-[#C7511F] hover:underline text-sm">Visit the {product.brand || 'Store'}</a>
          </div>
          
          <div className="flex items-center gap-4 text-sm border-b border-gray-200 pb-2">
            <span className="text-[#007185]">Ratings: {product.ratings || 0} ★</span>
            <span className="text-[#007185]">SKU: {product.sku}</span>
          </div>

          <div className="flex items-baseline gap-2">
            <span className="text-sm align-top mt-1">₹</span>
            <span className="text-3xl font-medium">{product.price || 0}</span>
            {product.previous_price && (
              <span className="text-sm text-gray-500 line-through ml-2">M.R.P: ₹{product.previous_price}</span>
            )}
          </div>

          <div className="text-sm text-gray-700 space-y-1">
            <p>Inclusive of all taxes</p>
            <p>GST: <span className="font-bold">{product.gstPercentage || 0}%</span></p>
            <p>Delivery: <span className="font-bold">₹{product.shippingCharge || 0}</span></p>
          </div>

          <div className="mt-4">
            <h3 className="font-bold text-gray-900 mb-2">Product Information</h3>
            <table className="text-sm w-full max-w-md">
              <tbody>
                <tr className="border-b border-white"><td className="font-bold py-1 bg-gray-50 w-1/2 px-2">Brand</td><td className="py-1 px-2">{product.brand || 'N/A'}</td></tr>
                <tr className="border-b border-white"><td className="font-bold py-1 bg-gray-50 w-1/2 px-2">Material</td><td className="py-1 px-2">{getText(product.material) || '—'}</td></tr>
                <tr className="border-b border-white"><td className="font-bold py-1 bg-gray-50 w-1/2 px-2">Finish Type</td><td className="py-1 px-2">{getText(product.finishType) || '—'}</td></tr>
                <tr className="border-b border-white"><td className="font-bold py-1 bg-gray-50 w-1/2 px-2">Weight</td><td className="py-1 px-2">{product.weight || '—'}</td></tr>
                <tr className="border-b border-white"><td className="font-bold py-1 bg-gray-50 w-1/2 px-2">Origin</td><td className="py-1 px-2">{getText(product.origin) || '—'}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 border-t border-gray-200 pt-4">
            <h3 className="font-bold text-gray-900 mb-2 text-base">About this item</h3>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-800">
              {product.features?.map((feature, i) => (
                <li key={i}>{getText(feature)}</li>
              ))}
              {(!product.features || product.features.length === 0) && (
                <li className="text-gray-500 italic">No features listed.</li>
              )}
            </ul>
          </div>
        </div>

        {/* Right Buy Box Style Panel */}
        <div className="border border-gray-300 rounded-lg p-4 bg-white flex flex-col gap-3 h-fit">
          <span className="text-lg font-medium text-black">₹{product.price || 0}</span>
          
          <div className="text-sm">
            {product.stock_status === 'out of stock' ? (
              <span className="text-[#B12704] font-medium text-lg">Currently unavailable.</span>
            ) : (
              <span className="text-[#007600] font-medium text-lg capitalize">{product.stock_status}</span>
            )}
          </div>
          
          <div className="text-sm mt-2">
            <div className="grid grid-cols-[80px_1fr] gap-1">
              <span className="text-gray-500">Ships from</span>
              <span className="text-gray-900 font-medium">Bharat Karigari</span>
              <span className="text-gray-500">Sold by</span>
              <span className="text-[#007185] hover:underline cursor-pointer">{product.uploadedBy || 'Supplier'}</span>
            </div>
          </div>

          <hr className="my-2" />
          
          <h3 className="font-bold text-sm">Return Policy</h3>
          <p className="text-sm text-[#007185] hover:underline cursor-pointer">
            {getText(product.return_policy) || 'Standard Return Policy'}
          </p>

          <h3 className="font-bold text-sm mt-2">Care Instructions</h3>
          <p className="text-sm text-gray-700">
            {getText(product.careInstruction) || 'Handle with care.'}
          </p>
        </div>

      </div>
      
      {/* Description & Video Section at bottom */}
      <div className="max-w-[1500px] mx-auto p-6 border-t border-gray-300">
         <h2 className="text-[#C7511F] font-bold text-lg mb-4">Product Description</h2>
         <p className="text-sm text-gray-800 whitespace-pre-wrap max-w-4xl leading-relaxed">
           {getText(product.description)}
         </p>
         
         {product.productVideo && (
            <div className="mt-8 max-w-2xl">
              <h2 className="text-gray-900 font-bold text-lg mb-4">Product Video</h2>
              <video src={product.productVideo} controls className="w-full rounded-sm border border-gray-300 shadow-sm" />
            </div>
         )}
      </div>

      <DeleteAlertModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        dltType="product"
        dltName={getText(product?.name)}
        onConfirm={handleDelete}
        dltLoading={deleteLoading}
      />
    </div>
  );
};

export default AdminProductDetails;

